import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Home,
  Upload,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Loader2,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import PropertyPortalUploadDialog from "@/components/properties/PropertyPortalUploadDialog";
import { SEO } from "@/components/seo/SEO";

interface PortalInfo {
  id: string;
  property_id: string;
  client_name: string;
  client_email: string | null;
  property_title: string;
  property_address: string;
  property_city: string;
  transaction_type: string;
}

interface PortalItem {
  id: string;
  doc_type: string;
  label: string;
  is_required: boolean;
  is_uploaded: boolean;
  sort_order: number;
  document_id: string | null;
}

const PropertyPortal = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [portal, setPortal] = useState<PortalInfo | null>(null);
  const [items, setItems] = useState<PortalItem[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PortalItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!token) {
      setError("Lien invalide");
      setLoading(false);
      return;
    }

    try {
      // Fetch portal info
      const { data: portalData, error: portalError } = await supabase.rpc(
        "get_property_portal_by_token",
        { p_token: token }
      );

      if (portalError) throw portalError;

      if (!portalData || portalData.length === 0) {
        setError("Ce lien d'accès n'est pas valide ou a expiré");
        setLoading(false);
        return;
      }

      setPortal(portalData[0]);

      // Fetch portal items
      const { data: itemsData, error: itemsError } = await supabase.rpc(
        "get_property_portal_items_by_token",
        { p_token: token }
      );

      if (itemsError) throw itemsError;
      setItems(itemsData || []);
    } catch (err) {
      console.error("Error fetching portal data:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUploadClick = (item: PortalItem) => {
    setSelectedItem(item);
    setUploadDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement du portail...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <SEO
          title="Portail inaccessible | EasyFinder"
          description="Ce portail n'est pas accessible"
          noIndex={true}
        />
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Accès refusé</h1>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const uploadedItems = items.filter((item) => item.is_uploaded);
  const pendingItems = items.filter((item) => !item.is_uploaded);
  const requiredPending = pendingItems.filter((item) => item.is_required);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      <SEO
        title={`Dossier ${portal?.property_title} | EasyFinder`}
        description="Déposez vos documents pour votre transaction immobilière"
        noIndex={true}
      />

      {/* Header */}
      <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <Home className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">{portal?.property_title}</h1>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {portal?.property_address}, {portal?.property_city}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Card */}
        <Card className="mb-6 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-primary/10 shrink-0">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-lg mb-1">
                  Bonjour {portal?.client_name}
                </h2>
                <p className="text-muted-foreground text-sm">
                  Bienvenue sur votre espace de dépôt de documents. Vous trouverez ci-dessous 
                  la liste des documents nécessaires pour votre{" "}
                  {portal?.transaction_type === "sale" ? "vente" : "location"}.
                  Déposez-les en cliquant sur chaque ligne.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-emerald-600">{uploadedItems.length}</div>
              <p className="text-xs text-muted-foreground">Déposés</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-amber-600">{requiredPending.length}</div>
              <p className="text-xs text-muted-foreground">Obligatoires restants</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-muted-foreground">{pendingItems.length}</div>
              <p className="text-xs text-muted-foreground">À déposer</p>
            </CardContent>
          </Card>
        </div>

        {/* Documents List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documents à fournir
            </CardTitle>
            <CardDescription>
              Cliquez sur un document pour le déposer
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {items.map((item) => {
                const isUploaded = item.is_uploaded;
                const StatusIcon = isUploaded ? CheckCircle2 : item.is_required ? Clock : FileText;
                const statusColor = isUploaded
                  ? "text-emerald-600"
                  : item.is_required
                  ? "text-amber-600"
                  : "text-muted-foreground";
                const bgColor = isUploaded
                  ? "bg-emerald-500/10"
                  : item.is_required
                  ? "bg-amber-500/10"
                  : "bg-muted";

                return (
                  <div
                    key={item.id}
                    className={cn(
                      "flex items-center justify-between p-4 rounded-lg border transition-colors",
                      !isUploaded && "border-dashed hover:border-primary/50 hover:bg-primary/5 cursor-pointer",
                      isUploaded && "bg-muted/30"
                    )}
                    onClick={() => !isUploaded && handleUploadClick(item)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2 rounded-lg", bgColor)}>
                        <StatusIcon className={cn("w-4 h-4", statusColor)} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{item.label}</p>
                          {item.is_required && !isUploaded && (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                              Obligatoire
                            </Badge>
                          )}
                        </div>
                        <p className={cn("text-sm", statusColor)}>
                          {isUploaded ? "Document déposé" : "À déposer"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {isUploaded ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUploadClick(item);
                          }}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Remplacer
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => handleUploadClick(item)}>
                          <Upload className="w-4 h-4 mr-1" />
                          Déposer
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}

              {items.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  Aucun document requis pour le moment
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          Portail sécurisé propulsé par EasyFinder
        </p>
      </main>

      {token && selectedItem && (
        <PropertyPortalUploadDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          token={token}
          item={selectedItem}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
};

export default PropertyPortal;
